import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Container } from 'ui/molecules/Container'
import { VehicleDetailsForm } from '../VehicleDetailsForm'
import { fleetApi } from 'api/fleet'
import { AddVehicleRequest } from 'api/fleet/types'
import { getDetailsBreadcrumbsList } from 'utils/getDetailsBreadcrumbsList'
import { useAppSelector } from 'redux/hooks'
import {
  isMigratedUserSelector,
  isOperatorSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { setNotificationMessage } from 'redux/notifications/slice'
import { useApiRequest } from 'hooks/useApiRequest'
import { SUCCESS_UPDATE_MESSAGE } from 'texts/common'
import { VEHICLE_DETAILS_BREADCRUMB, VEHICLE_TITLE } from 'texts/vehicleDetails'
import { OperatorActionButtons } from 'ui/components/OperatorActionButtons'
import {
  ACCOUNT_STATUSES,
  READONLY_STATUSES,
  REGISTRATION_STATUSES,
} from 'api/auth/constants'
import { URLS } from 'constants/urls'
import { useInitVehicleData } from '../../hooks/useInitVehicleData'
import { useCompanyId } from 'hooks/useCompanyId'
import { VehicleSubmitButton } from '../VehicleSubmitButton'
import { FormActionButtons } from 'ui/components/FormActionButtons'
import { VEHICLE_DETAILS_FORM_ID } from '../../constants'
import styles from './styles.module.scss'
import { useMigrationRedirect } from 'hooks/useMigrationRedirect'

export const VehicleEdit = () => {
  const params = useParams()
  const [initialData, setInitialData] = useState<Partial<AddVehicleRequest>>({})
  const isOperator = useAppSelector(isOperatorSelector)
  const { accountStatus, sectionsStatuses } = useAppSelector(userDataSelector)
  const isMigratedUser = useAppSelector(isMigratedUserSelector)
  const isMigratedUserFlow = isMigratedUser && !isOperator

  const isAccountApproved =
    isOperator || accountStatus === ACCOUNT_STATUSES.APPROVED

  const isIncomplete =
    !isOperator &&
    [
      REGISTRATION_STATUSES.INCOMPLETE,
      REGISTRATION_STATUSES.REQUESTED,
    ].includes(sectionsStatuses?.carsStatus || '')

  const vehicleId = params.id || ''

  const companyId = useCompanyId()

  const { vehicleDetailsData, isLoaded } = useInitVehicleData({
    supplierId: String(companyId),
  })

  const { redirect } = useMigrationRedirect()

  const updateVehicleResponse = useApiRequest(
    (vehicleId, data) => fleetApi.updateVehicleInfo(vehicleId, data),
    undefined,
    true,
    undefined,
    false
  )

  const getVehicleInfoRequest = useApiRequest((vehicleId) =>
    fleetApi.getVehicleInfo(vehicleId)
  )

  const breadcrumbList = getDetailsBreadcrumbsList(
    isOperator,
    String(companyId),
    'vehicles'
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    const getInfo = async () => {
      if (
        !isAccountApproved &&
        READONLY_STATUSES.includes(sectionsStatuses?.carsStatus || '')
      ) {
        navigate(`${URLS.FLEET}${URLS.VEHICLES}`)
        return
      }
      if (vehicleId) {
        const response = await getVehicleInfoRequest.apiRequest(vehicleId)
        if (response) {
          setInitialData(response.data.vehicleInfo)
        }
      }
    }
    getInfo()
  }, [])

  const handleSubmit = async (data: AddVehicleRequest) => {
    if (vehicleId) {
      const response = await updateVehicleResponse.apiRequest(vehicleId, data)
      if (response) {
        dispatch(
          setNotificationMessage({
            notificationMessage: SUCCESS_UPDATE_MESSAGE,
          })
        )
        if (isMigratedUserFlow) {
          redirect(companyId, 'carsStatus')
        }
        if (isAccountApproved) {
          const path = isOperator
            ? `${URLS.FLEET}${URLS.VEHICLES}/${companyId}`
            : `${URLS.FLEET}${URLS.VEHICLES}`
          navigate(path)
        } else {
          navigate(`${URLS.FLEET}${URLS.VEHICLES}/add`)
        }
      }
    }
  }

  const handleCancelClick = () => {
    if (isAccountApproved) {
      const path = isOperator
        ? `${URLS.FLEET}${URLS.VEHICLES}/${companyId}`
        : `${URLS.FLEET}${URLS.VEHICLES}`
      navigate(path)
    } else {
      navigate(`${URLS.FLEET}${URLS.VEHICLES}/add`)
    }
  }

  return (
    <Container
      title={VEHICLE_TITLE}
      breadcrumbList={breadcrumbList}
      currentPageLabel={VEHICLE_DETAILS_BREADCRUMB}
      withNavigation={isOperator}
      actionButtons={(id: string) => (
        <OperatorActionButtons companyId={id} section="Cars" />
      )}
      rightBlock={() => (
        <div className={styles['button-container']}>
          <FormActionButtons
            handleBack={handleCancelClick}
            loading={updateVehicleResponse.loading}
            formId={VEHICLE_DETAILS_FORM_ID}
          />
          {isIncomplete && <VehicleSubmitButton />}
        </div>
      )}
      loading={getVehicleInfoRequest.loading || !isLoaded}
    >
      <VehicleDetailsForm
        formValues={initialData}
        handleSubmit={handleSubmit}
        isLoading={updateVehicleResponse.loading}
        vehicleDetailsData={vehicleDetailsData}
        isEditing
      />
    </Container>
  )
}
